import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api/client';
import DashboardLayout from '../components/DashboardLayout';
import { Skeleton } from '../components/Skeleton';
import Icon from '../components/Icon';
import { EmptySignal } from '../components/illustrations';

const TYPE_META = {
  discord_webhook: { icon: 'hash', targetKey: 'target_discord', helpKey: 'help_discord', placeholder: 'https://discord.com/api/webhooks/...' },
  email: { icon: 'mail', targetKey: 'target_email', helpKey: 'help_email', placeholder: 'alarm@sunucunuz.com' },
  telegram: { icon: 'send', targetKey: 'target_telegram', helpKey: 'help_telegram', placeholder: '123456789' },
};

function maskTarget(type, target) {
  if (type === 'discord_webhook') {
    return target.replace(/(webhooks\/\d+\/)([\w-]+)/, (_, p, tok) => p + tok.slice(0, 4) + '…');
  }
  return target;
}

export default function Notifications() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState([]);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ type: 'discord_webhook', target: '' });
  const [formErr, setFormErr] = useState('');
  const [busy, setBusy] = useState(false);
  const [testState, setTestState] = useState({}); // id -> { status, msg }

  const load = useCallback(async () => {
    const res = await api.listChannels();
    setChannels(res.channels || []);
  }, []);

  useEffect(() => {
    load().finally(() => setLoading(false));
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setFormErr('');
    setBusy(true);
    try {
      await api.createChannel({ type: form.type, target: form.target.trim() });
      setForm({ type: form.type, target: '' });
      setAdding(false);
      await load();
    } catch (err) {
      setFormErr(err.code === 'invalid_target' ? err.message : t('notifications.err_generic'));
    } finally {
      setBusy(false);
    }
  };

  const toggle = async (ch) => {
    await api.updateChannel(ch.id, { enabled: !ch.enabled });
    load();
  };

  const remove = async (ch) => {
    if (!window.confirm(t('notifications.delete_confirm'))) return;
    await api.deleteChannel(ch.id);
    load();
  };

  const runTest = async (ch) => {
    setTestState((s) => ({ ...s, [ch.id]: { status: 'testing' } }));
    try {
      const res = await api.testChannel(ch.id);
      setTestState((s) => ({
        ...s,
        [ch.id]: res.ok
          ? { status: 'ok', msg: t('notifications.test_ok') }
          : { status: 'fail', msg: `${t('notifications.test_fail')}: ${res.error || ''}` },
      }));
    } catch (err) {
      setTestState((s) => ({ ...s, [ch.id]: { status: 'fail', msg: `${t('notifications.test_fail')}: ${err.message}` } }));
    }
    setTimeout(() => setTestState((s) => ({ ...s, [ch.id]: undefined })), 6000);
  };

  const meta = TYPE_META[form.type];

  return (
    <DashboardLayout title={t('notifications.title')} crumb={t('dashboard.title')} heading={false}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 className="page-title">{t('notifications.title')}</h1>
          <p className="page-sub">{t('notifications.subtitle')}</p>
        </div>
        {!adding && (
          <button className="btn btn-sm" onClick={() => setAdding(true)}>
            <Icon name="plus" size={14} /> {t('notifications.add')}
          </button>
        )}
      </div>

      {adding && (
        <div className="card" style={{ marginBottom: 'var(--sp-4)' }}>
          <form onSubmit={submit}>
            <div className="field">
              <label>{t('notifications.type')}</label>
              <div className="lang-toggle" style={{ borderRadius: 'var(--r-md)' }}>
                {Object.keys(TYPE_META).map((tp) => (
                  <button type="button" key={tp} className={form.type === tp ? 'active' : ''}
                    style={{ padding: '8px 14px', fontSize: 12 }}
                    onClick={() => setForm({ type: tp, target: '' })}>
                    {t(`notifications.type_${tp}`)}
                  </button>
                ))}
              </div>
            </div>
            <div className="field">
              <label>{t(`notifications.${meta.targetKey}`)}</label>
              <div className="control">
                <input
                  type="text" value={form.target} placeholder={meta.placeholder}
                  onChange={(e) => setForm((f) => ({ ...f, target: e.target.value }))} required autoFocus
                />
              </div>
              <div className="hint">{t(`notifications.${meta.helpKey}`)}</div>
            </div>
            {formErr && <div className="form-error"><Icon name="alert-triangle" size={14} /> {formErr}</div>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-sm" disabled={busy}>{busy ? '…' : t('notifications.save')}</button>
              <button type="button" className="btn btn-ghost btn-sm" onClick={() => { setAdding(false); setFormErr(''); }}>
                {t('notifications.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      {loading ? (
        <div className="card">
          {[0, 1].map((i) => <Skeleton key={i} className="sk-text" style={{ margin: '16px 0' }} />)}
        </div>
      ) : channels.length === 0 && !adding ? (
        <div className="empty-state">
          <EmptySignal />
          <h3>{t('notifications.empty_title')}</h3>
          <p>{t('notifications.empty_desc')}</p>
          <button className="btn btn-subtle btn-sm" style={{ marginTop: 'var(--sp-4)' }} onClick={() => setAdding(true)}>
            <Icon name="plus" size={14} /> {t('notifications.add')}
          </button>
        </div>
      ) : (
        <div className="card" style={{ padding: 0 }}>
          <div className="list">
            {channels.map((ch) => {
              const m = TYPE_META[ch.type] || {};
              const ts = testState[ch.id];
              return (
                <div className="list-row" key={ch.id} style={{ padding: 'var(--sp-4)', flexWrap: 'wrap', gap: 12 }}>
                  <span className="avatar" style={{ background: 'var(--surface-3)', color: 'var(--text-2)', border: '1px solid var(--border)' }}>
                    <Icon name={m.icon || 'bell'} size={15} />
                  </span>
                  <div className="row-main">
                    <div style={{ fontWeight: 500 }}>{t(`notifications.type_${ch.type}`)}</div>
                    <div style={{ color: 'var(--text-3)', fontSize: 12.5, wordBreak: 'break-all' }}>
                      {maskTarget(ch.type, ch.target)}
                    </div>
                    {ts && ts.msg && (
                      <div style={{ fontSize: 12, marginTop: 4, color: ts.status === 'ok' ? 'var(--ok)' : 'var(--crit)' }}>
                        {ts.msg}
                      </div>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <button className="btn btn-subtle btn-sm" onClick={() => runTest(ch)} disabled={ts?.status === 'testing'}>
                      <Icon name="send" size={12} /> {ts?.status === 'testing' ? t('notifications.testing') : t('notifications.test')}
                    </button>
                    <button
                      className={`badge ${ch.enabled ? 'ok' : ''}`}
                      style={{ cursor: 'pointer', border: 'none' }}
                      onClick={() => toggle(ch)}
                    >
                      <span className={`pulse is-static ${ch.enabled ? 'ok' : 'idle'}`} />
                      {ch.enabled ? t('notifications.enabled') : t('notifications.disabled')}
                    </button>
                    <button className="btn btn-ghost btn-sm" onClick={() => remove(ch)} aria-label={t('notifications.delete')}>
                      <Icon name="trash" size={13} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
